import { cx } from "@linaria/core";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
  RefObject,
} from "react";
import { useRef, useState } from "react";
import { TransitionGroup } from "react-transition-group";

import * as styles from "./icon-button.style";
import { ColorTokenInput } from "../css/token.color";
import { resets } from "../css-tokens/resets";
import { CSSRefTransition } from "./transition.css";
import { CheckmarkIcon } from "../icons";
import { useLoaderState } from "../hooks/useLoaderState";
import { TooltipProps, Tooltip } from "./tooltip";

export { selectors as iconButtonSelectors } from "./icon-button.style";

type Props = {
  ref?: RefObject<HTMLElement | null>;

  label: ReactNode;
  tooltip?: Pick<TooltipProps, "body" | "ariaType"> & {
    offsetPx?: number;
    placement?: TooltipProps["placement"];
    disableFallbackPlacements?: boolean;
  };
  active?: boolean;
  icon: ReactNode;
  iconKey?: string;
  activeIcon?: ReactNode;

  state?: "success" | "partial-success" | "pending";

  /**
   * By default the label is only visible through tooltips, however this prop
   * forces the label to be rendered inline
   */
  visibleLabel?: boolean;
  size?: "small";
  color?: ColorTokenInput;

  children?: ReactNode | ReactNode[];

  async?: {
    showThrobberWhilePending?: boolean;
    showStatesAfterResult?: boolean;
  };

  onClick?(evt: React.MouseEvent<HTMLElement>): void | Promise<void>;
} & (
  | ({ as?: "button" } & Omit<
      ButtonHTMLAttributes<HTMLButtonElement>,
      "onClick"
    >)
  | ({ as?: "a" } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "onClick">)
);

/**
 * An icon button represents a button primarily visualised through an icon
 * used to trigger a specific action, often related to a parent component.
 *
 * As such this is often used in conjunction with list items/table rows which
 * represents interactable items.
 */
export function IconButton({
  as,
  children,
  label,
  icon,
  iconKey,
  activeIcon,
  onClick,
  visibleLabel,
  active,
  tooltip,
  state: forcedState,
  size,
  color,
  className,
  async,
  ...props
}: Props): React.JSX.Element {
  const internalRef = useRef<HTMLElement>(null);
  const buttonRef = props.ref ?? internalRef;

  const [internalState, setState] = useState<
    "default" | "pending" | "success" | "error"
  >("default");
  const state = forcedState || internalState;

  const showThrobber = useLoaderState(
    !!async?.showThrobberWhilePending && state === "pending",
    {
      delayMs: 250,
      durationMs: 375,
    },
  );

  const showThrobberNow = showThrobber.current;

  const handleClick = async (evt: React.MouseEvent<HTMLButtonElement>) => {
    setState("pending");

    try {
      await onClick?.(evt);

      setState("success");
    } catch {
      setState("error");
    } finally {
      setTimeout(() => {
        setState("default");
      }, 2500);
    }
  };

  const resolvedContentKey = (() => {
    if (showThrobberNow) {
      return "throbber";
    }

    if (
      async?.showStatesAfterResult &&
      !visibleLabel &&
      (state === "success" || state === "error")
    ) {
      return state;
    }

    return activeIcon && active ? "active" : "default";
  })();

  const content = (() => {
    switch (resolvedContentKey) {
      case "default":
        return icon;

      case "active":
        return activeIcon;

      case "throbber":
        return <span className={styles.throbberCls} />;

      case "success":
        return (
          <span className={styles.successCls}>
            <CheckmarkIcon height={7.5} />
          </span>
        );

      case "error":
        return <span className={styles.errorCls} />;
    }
  })();

  const Comp = as === "a" ? "a" : "button";

  return (
    <Tooltip
      containerRef={buttonRef}
      placement={tooltip?.placement ?? "top"}
      disableFallbackPlacements={tooltip?.disableFallbackPlacements}
      body={tooltip?.body || label}
      ariaType={tooltip?.ariaType || "label"}
      disabled={active || (visibleLabel && !tooltip?.body)}
      // Negate padding used to extend clickable area
      offsetPx={tooltip?.offsetPx ?? 5}
    >
      {(ref: RefObject<HTMLButtonElement | HTMLAnchorElement | null>) => (
        <Comp
          {...props}
          // @ts-expect-error We allow our button ref to contain a generic
          // HTMLElement.
          ref={ref}
          onClick={async ? handleClick : onClick}
          style={styles.tokens.__override({ color })}
          className={cx(
            resets.rootCls,
            className,
            styles.buttonCls,
            size === "small" && styles.modifiers.small.className,
            visibleLabel && styles.modifiers.withLabel.className,
          )}
          data-active={active}
          disabled={
            ("disabled" in props && props.disabled) || state === "pending"
          }
          data-state={async?.showStatesAfterResult ? state : "default"}
        >
          <span className={styles.iconsContainerCls}>
            <TransitionGroup component={null}>
              <CSSRefTransition
                key={
                  resolvedContentKey === "default"
                    ? iconKey || "default"
                    : resolvedContentKey
                }
                timeout={styles.iconTransitionDurationMs}
                classNames={styles.iconWrapperCls}
              >
                {(ref) => (
                  <span ref={ref} className={cx(styles.iconWrapperCls)}>
                    {content}
                  </span>
                )}
              </CSSRefTransition>
            </TransitionGroup>
          </span>

          {visibleLabel && (
            <span className={styles.label}>
              <span>{label}</span>

              {async?.showStatesAfterResult && state === "success" && (
                <span className={styles.checkmark}>
                  <CheckmarkIcon height={7} strokeWidth={3} />
                </span>
              )}
            </span>
          )}

          {children}
        </Comp>
      )}
    </Tooltip>
  );
}
